import { useState, useEffect, useCallback } from 'react';
import { dashboardService, DashboardData, SystemStatus } from '../services/dashboard';

interface UseDashboardReturn {
  dashboardData: DashboardData | null;
  systemStatus: SystemStatus | null;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  lastUpdated: Date | null;
}

export const useDashboard = (autoRefreshInterval?: number): UseDashboardReturn => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { dashboardData: newDashboardData, systemStatus: newSystemStatus } = 
        await dashboardService.refreshData();

      setDashboardData(newDashboardData);
      setSystemStatus(newSystemStatus);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Initial data load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefreshInterval) return;

    const interval = setInterval(() => {
      fetchData();
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshInterval, fetchData]);

  return {
    dashboardData,
    systemStatus,
    loading,
    error,
    refreshData,
    lastUpdated
  };
};